import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { apiService } from '@/services/api'

// Tema visual da conta (redesign 2026-07-18, docs/theme-redesign/).
// O app e sempre light; estes temas trocam a paleta inteira via data-theme
// no <html>. O dark existe apenas dentro da mesa (GameAppearanceContext).
export type AccountTheme = 'azul' | 'manuscrito' | 'arcano' | 'sinal'

export const ACCOUNT_THEMES: AccountTheme[] = ['azul', 'manuscrito', 'arcano', 'sinal']

const DEFAULT_THEME: AccountTheme = 'azul'

// Mesma chave da era light/dark: valores legados sao sanitizados para 'azul'.
const THEME_STORAGE_KEY = 'rpgia-theme'

function sanitizeTheme(value: string | null): AccountTheme {
  return (ACCOUNT_THEMES as string[]).includes(value ?? '') ? (value as AccountTheme) : DEFAULT_THEME
}

interface ThemeContextType {
  theme: AccountTheme
  setTheme: (theme: AccountTheme, opts?: { persist?: boolean }) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<AccountTheme>(() =>
    sanitizeTheme(localStorage.getItem(THEME_STORAGE_KEY))
  )

  // O script anti-flash do index.html ja aplicou o data-theme antes do primeiro
  // paint; este effect cobre trocas em runtime e o caso de storage legado.
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  // persist=false: alinhamento vindo DO backend (nao ecoar de volta).
  const setTheme = (newTheme: AccountTheme, opts?: { persist?: boolean }) => {
    setThemeState(newTheme)
    if (opts?.persist !== false) {
      // Fire-and-forget: o tema local ja mudou; falha de rede nao deve travar a UI.
      apiService.updateCurrentUser({ theme: newTheme }).catch(() => undefined)
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Alinha o tema local ao da conta apos o login/checagem de auth.
// O backend e a fonte de verdade; o localStorage e so cache anti-flash.
// Aceita qualquer shape de usuario que carregue theme (ha dois tipos User no repo).
export function useSyncAccountTheme(user: { theme?: string } | null) {
  const { theme, setTheme } = useTheme()
  const accountTheme = user?.theme

  useEffect(() => {
    if (accountTheme && accountTheme !== theme && (ACCOUNT_THEMES as string[]).includes(accountTheme)) {
      setTheme(accountTheme as AccountTheme, { persist: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountTheme])
}
